import { withStyles } from '@material-ui/core';
import React from 'react';
import styles from '../NewJob.styles';
import { IServiceItemsPanelHandle, IServiceItemsPanelProps, IServiceItemData } from '../NewJobModal.properties';
import { NEW_JOB_DATA_GRID_HEIGHT, NEW_JOB_LOADING_INDICATOR_HEIGHT_WIDTH } from '../NewJobModal.constants';
import DataGrid, { Button, Column, Scrolling, LoadPanel, Editing } from 'devextreme-react/data-grid';
import { AddServiceItemModal } from './AddServiceItemModal';
import { isNull } from 'utils/utils';
import { cloneDeep } from 'lodash';

function ServiceItemsPanel(props: IServiceItemsPanelProps, ref: React.Ref<IServiceItemsPanelHandle>): JSX.Element {
    const {
        data,
        classes,
        loading,
        CustomerId,
        setData,
        changeConfirmationDialog,
        onDeleteRow,
    } = props;

    const dataGridRef = React.useRef<DataGrid>();
    const [addItemModalOpen, setAddItemModalOpen] = React.useState<boolean>(false);
    const [itemModalEditMode, setItemModalEditMode] = React.useState<boolean>(false);
    const [addItemData, setAddItemData] = React.useState<IServiceItemData>(null);

    React.useImperativeHandle(
        ref,
        () => ({
            AddItem(): void {
                setAddItemModalOpen(true);
                setAddItemData(null);
            }
        })
    );

    React.useEffect(
        () => {
            if (dataGridRef.current) {
                if (loading) {
                    dataGridRef.current.instance.beginCustomLoading('');
                } else {
                    dataGridRef.current.instance.endCustomLoading();
                }
            }
        },
        [loading]
    );

    function onEditClicked(rowData: any): void {
        setAddItemModalOpen(true);
        setItemModalEditMode(true);
        setAddItemData(rowData.row.data);
    }

    function handleAddActivity(itemData: IServiceItemData): void {
        if (!isNull(itemData)) {

            if (itemModalEditMode) {
                const dataIndex = data.findIndex((a) => a.Id === itemData.Id);
                const clonedData = cloneDeep(data);
                clonedData.splice(dataIndex, 1, itemData);
                setData(clonedData);
            } else {

                const newId = isNull(data) ? 1 : Math.max(...data.map((d) => d.Id)) + 1;
                setData([...data, { ...itemData, Id: newId }]);
            }
        }

        setAddItemModalOpen(false);
        setItemModalEditMode(false);
    }

    const deleteClick = ({row}) => {
        changeConfirmationDialog({
            open: true,
            title: 'Delete selected record',
            message: 'Are you sure you want to delete the selected record?',
            okLabel: 'Delete',
            onCancel: () => null,
            onOk: () => {
                onDeleteRow(row.data.Id);
            }
        });
    };

    return (
        <div className={classes.serviceAgreementItemContainer}>
            <div className={`${classes.fullWidth} ${classes.heading}`}>Add a combination of Service Items and Job Templates to the job.</div>
            <DataGrid
                style={styles.fullWidth}
                keyExpr={'Id'}
                ref={dataGridRef}
                height={NEW_JOB_DATA_GRID_HEIGHT}
                dataSource={data}
                repaintChangesOnly={true}
                remoteOperations={true}
                columnResizingMode={'nextColumn'}
                allowColumnReordering={true}
                noDataText=''
                allowColumnResizing={true}
                showBorders={false}
                sorting={{
                    mode: 'none'
                }}
                hoverStateEnabled={true}
            >
                <Editing
                    refreshMode={'reshape'}
                    mode='row'
                    allowAdding={false}
                    allowDeleting={true}
                    confirmDelete={true}
                    allowUpdating={false}
                />
                <Scrolling mode='virtual' />
                <Column
                    caption={'Service Item'}
                    calculateCellValue={(d) => isNull(d.ServiceNoDisplay) ? 'No ServiceItem' : `${d.ServiceNoDisplay} - ${d.ServiceNoDisplayLabel}`}
                    alignment={'left'}
                    minWidth={'45%'}
                    fixed={true}
                >
                </Column>
                <Column
                    caption={'Job Template'}
                    calculateCellValue={(d) => (`${d.TemplateCode} - ${d.TemplateCodeLabel}`)}
                    minWidth={'45%'}
                >
                </Column>
                <LoadPanel shading={false} height={NEW_JOB_LOADING_INDICATOR_HEIGHT_WIDTH} width={NEW_JOB_LOADING_INDICATOR_HEIGHT_WIDTH} text={''} showPane={false} />
                <Column type={'buttons'} width={'10%'} allowResizing={false}>
                    <Button icon={'edit'} onClick={onEditClicked} />
                    <Button name={'delete'} icon={'trash'} onClick={deleteClick} />
                    <Button name={'undelete'} icon={'undo'} />
                </Column>
            </DataGrid>
            {addItemModalOpen && <AddServiceItemModal open={addItemModalOpen} CustomerId={CustomerId} data={addItemData} editMode={itemModalEditMode} handleClose={handleAddActivity} />}
        </div>
    );
}

export default withStyles(styles, { index: 1 })(React.forwardRef(ServiceItemsPanel));
